<template>
  <div class="button-field-renderer">
    <PButton
      style="width: 100%"
      :type="campo.action?.variant ?? 'primary'"
      :disabled="disabled"
      :loading="loading"
      @click="handleClick"
      suffix-icon="arrow_outward"
    >
      {{ campo.label }}
    </PButton>
    <div v-if="actionFeedback" class="action-feedback">
      <PBannerBase type="info">
        <template #title>
          <div class="action-feedback__message">{{ actionFeedback }}</div>
        </template>
      </PBannerBase>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PBannerBase, PButton } from "@f360-dev/f360-prisma";
import { ElMessageBox } from "element-plus";
import { computed, ref } from "vue";
import { IDefaultHttpFinancasResponse } from "@/core/@types/types";
import { DITypes } from "@/core/@types/types";
import { IToastNotification } from "@/core/composables/notification/types";
import { ILogoutGateway } from "@/core/services/user/logout/LogoutGateway";
import { diContainer } from "@/inversify.config";
import { TIntegracaoCampoModel } from "../../../@types/IntegracaoCampoModel";
import usePayloadResolver from "../../../composables/dynamicForm/handlers/usePayloadResolver/usePayloadResolver";
import {
  TIntegracaoAction,
  TIntegracaoActionOutcome,
  TIntegracaoActionSetField,
  TIntegracaoActionTemplateParameterRef
} from "../../../services/CadastroDeIntegracaoGateway";

const props = defineProps<{
  campo: TIntegracaoCampoModel;
  formValues?: Record<string, unknown>;
  actionContext?: Record<string, unknown>;
  setFieldValue?: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
  executeFieldAction?: (
    metodo: string,
    endpoint: string,
    payload: Record<string, unknown>
  ) => Promise<IDefaultHttpFinancasResponse<unknown> | undefined>;
  requestCloseDrawer?: () => void;
  requestReloadForm?: () => Promise<void>;
}>();

const loading = ref(false);
const actionFeedback = ref("");
const logoutGateway = diContainer.get<ILogoutGateway>(DITypes.USER_LOGOUT);
const notification = diContainer.get<IToastNotification<string>>(
  DITypes.NOTIFICATION
);
const { buildPayload: resolvePayload } = usePayloadResolver();

const fieldHasValue = (fieldName: string): boolean => {
  const value = props.formValues?.[fieldName];

  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;

  return value !== null && value !== undefined && value !== false;
};

const contextValuesAreAvailable = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.every(contextValuesAreAvailable);
  if (!value || typeof value !== "object") return true;

  const node = value as Record<string, unknown>;
  if (typeof node.Context === "string") {
    const contextValue = props.actionContext?.[node.Context];
    if (
      contextValue === null ||
      contextValue === undefined ||
      contextValue === ""
    )
      return false;
  }

  return Object.values(node).every(contextValuesAreAvailable);
};

const canRunAction = computed(() => {
  const requiredFields = props.campo.action?.enableWhen ?? [];

  return (
    requiredFields.every(fieldHasValue) &&
    contextValuesAreAvailable(props.campo.action?.payload)
  );
});

const disabled = computed(
  () =>
    loading.value ||
    props.campo.desabilitado === true ||
    !canRunAction.value
);

const resolveTemplateParameter = (
  parameter: TIntegracaoActionTemplateParameterRef
): unknown => {
  if (typeof parameter.CampoId === "string") {
    return props.formValues?.[parameter.CampoId] ?? parameter.Valor;
  }

  return parameter.Valor;
};

const buildPayload = (): Record<string, unknown> =>
  resolvePayload(
    props.campo.action?.payload,
    props.formValues,
    props.actionContext
  );

const applyOutcome = (outcome: TIntegracaoActionOutcome | null | undefined) => {
  outcome?.setFields?.forEach((field: TIntegracaoActionSetField) => {
    props.setFieldValue?.(field.CampoId, field.Valor, false);
  });
};

const showSuccessMessage = (message: string, action: TIntegracaoAction) => {
  if (action.postAction?.type === "closeDrawer") {
    notification.showNotification(message, {
      type: "success",
      timeout: 3000
    });

    return;
  }

  actionFeedback.value = message;
};

const showErrorMessage = async (message: string) => {
  try {
    await ElMessageBox.alert(message, "", {
      type: "error",
      confirmButtonText: "OK"
    });
  } catch {
    return;
  }
};

const confirmAction = async (action: TIntegracaoAction) => {
  const confirm = action.confirm;

  if (!confirm?.message) return;

  await ElMessageBox.confirm(confirm.message, confirm.title ?? "", {
    type: "warning",
    confirmButtonText: confirm.confirmButtonText ?? "Continuar",
    cancelButtonText: confirm.cancelButtonText ?? "Cancelar"
  });
};

const buildOpenUrl = (action: TIntegracaoAction): string => {
  const openUrl = action.openUrl;
  const template = openUrl?.template;

  if (!template) throw new Error("Não foi possível montar a URL da ação.");

  const parameters = openUrl.parameters ?? {};

  return Object.entries(parameters).reduce((url, [key, parameter]) => {
    const resolvedValue = resolveTemplateParameter(parameter);
    const serializedValue =
      resolvedValue === null || resolvedValue === undefined
        ? ""
        : String(resolvedValue);

    return url.replaceAll(`{${key}}`, encodeURIComponent(serializedValue));
  }, template);
};

const runPostAction = async (action: TIntegracaoAction) => {
  if (action.postAction?.type === "logout") {
    await logoutGateway.logout();

    return;
  }

  if (action.postAction?.type === "closeDrawer") {
    props.requestCloseDrawer?.();

    return;
  }

  if (action.postAction?.type === "reloadForm") {
    await props.requestReloadForm?.();
  }
};

const messageFromOutcome = (
  outcome: TIntegracaoActionOutcome | null | undefined,
  response: IDefaultHttpFinancasResponse<unknown> | undefined,
  fallbackMessage: string
): string => {
  const responseRecord = response as Record<string, unknown> | undefined;
  const fieldMessage = outcome?.messageField
    ? responseRecord?.[outcome.messageField]
    : null;

  if (typeof fieldMessage === "string" && fieldMessage) return fieldMessage;
  if (typeof outcome?.message === "string" && outcome.message)
    return outcome.message;
  if (response?.Error) return response.Error;

  return fallbackMessage;
};

const handleActionClick = async () => {
  const action = props.campo.action;
  if (!action || !props.campo.endpoint || !props.executeFieldAction) return;

  loading.value = true;
  actionFeedback.value = "";
  try {
    const response = await props.executeFieldAction(
      action.method ?? "POST",
      props.campo.endpoint,
      buildPayload()
    );

    if (response?.Ok) {
      const successOutcome = action.result?.success;
      applyOutcome(successOutcome);

      const successMessage = messageFromOutcome(successOutcome, response, "");

      if (successMessage) showSuccessMessage(successMessage, action);

      await runPostAction(action);

      return;
    }

    const errorOutcome = action.result?.error;
    applyOutcome(errorOutcome);
    await showErrorMessage(
      messageFromOutcome(
        errorOutcome,
        response,
        "Não foi possível executar a ação."
      )
    );

    if (action.postAction?.type === "reloadForm")
      await runPostAction(action);
  } catch (error) {
    applyOutcome(action.result?.error);
    await showErrorMessage((error as Error).message);
  } finally {
    loading.value = false;
  }
};

const handleOauthRedirectClick = async () => {
  const action = props.campo.action;
  if (!action) return;

  loading.value = true;
  try {
    await confirmAction(action);

    const url = buildOpenUrl(action);
    const target = action.openUrl?.target ?? "_blank";
    const features = action.openUrl?.features ?? "noopener";

    window.open(url, target, features);
    await runPostAction(action);
  } catch (error) {
    if (error === "cancel" || (error as Error)?.message === "cancel") return;

    await showErrorMessage(
      (error as Error)?.message ?? "Não foi possível executar a ação."
    );
  } finally {
    loading.value = false;
  }
};

const handleClick = async () => {
  if (props.campo.action?.type === "oauthRedirect") {
    await handleOauthRedirectClick();

    return;
  }

  if (props.campo.action) {
    await handleActionClick();

    return;
  }

  if (!props.campo.endpoint) return;
  window.open(props.campo.endpoint, "_blank");
};
</script>

<style scoped lang="scss">
.button-field-renderer {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.action-feedback {
  width: 100%;
  align-self: stretch;
  text-align: left;
}

.action-feedback__message {
  width: 100%;
  color: inherit;
  font-size: 13px;
  font-weight: 300;
  text-align: left;
}
</style>
